/**
 * - To read more about Tableau authentication read this article: https://help.tableau.com/current/server/en-us/trusted_auth.htm
 * - For Dealing with single signon on local env go through this article:
 * https://k2labs.atlassian.net/wiki/spaces/AM/pages/1751482426/Tableau+-+Dealing+with+SSO+in+local+env
 */
import { Observable, Observer, interval, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ServerApi } from '../../services/server-api';
import { extractResponseBody } from '../../lib/utils';
import { CookieKey } from '@lib/cookie-keys.const';

export interface ITableauAuthTicketResponse {
  ticket: string;
}

// Strings to display when events happen. You can obfuscate as necessary
export const IMAGE_REDEMPTION_FAILURE_MESSAGE = 'Image redemption failed';

// Timeout parameters for how long to go between extensions of the auth by redeeming new ticket
// Check for Timeout Cookie
const PING_MINUTES = 10;
const PING_MILLI_SECONDS = 1000 * 60 * PING_MINUTES;
export const TABLEAU_TIME_OUT_COOKIE: CookieKey = CookieKey.TABLEAU_TIME_OUT_COOKIE;

@Injectable({
  providedIn: 'root',
})
export class TableauAuthService {
  private authToken: string;
  private currentVizArguments: {
    serverBaseUrl: string,
    tableauSite: string,
    workbook: string,
    view: string,
  };
  private tokenRefreshProcessStarted: boolean = false;
  private sessionRefreshPoolingSubscription: Subscription;

  constructor(private api: ServerApi) {}

  public authThenLoad(
    serverBaseUrl: string,
    tableauSite: string,
    workbook: string,
    view: string,
    shouldRefreshCookie = false,
  ): Observable<void> {
    this.currentVizArguments = { serverBaseUrl, tableauSite, workbook, view };
    return new Observable<void>((observer: Observer<void>) => {
      // Check if the cookie saying the last auth ping is passed its timeout
      if (this.isAuthExpired() || shouldRefreshCookie) {
        this.getTableauTrustedToken()
          .then((response: ITableauAuthTicketResponse) => {
            this.authToken = response.ticket;
            const fullAuthUrl = this.buildAuthUrlPng(serverBaseUrl, tableauSite, workbook, view, this.authToken);
            // This returns a Promise, so we return it out of the function to keep the chain of Promises going
            return this.imgLoadPromise(fullAuthUrl);
          })
          .then(() => {
            // If the image request completes correctly, you should have a session. Update the timeout cookie
            this.extendTimeoutCookie();
            observer.next();
            observer.complete();
            if (!this.tokenRefreshProcessStarted) this.refreshTrustedToken();
          })
          .catch(error => {
            observer.error(error);
            observer.complete();
          });
      } else {
        observer.next();
        observer.complete();
        if (!this.tokenRefreshProcessStarted) this.refreshTrustedToken();
      }
    });
  }

  public stopSessionPooling(): void {
    if (this.sessionRefreshPoolingSubscription) {
      this.tokenRefreshProcessStarted = false;
      this.sessionRefreshPoolingSubscription.unsubscribe();
    }
  }

  public invalidateTimeoutCookie(): void {
    this.setCookie(TABLEAU_TIME_OUT_COOKIE, '', 0);
  }

  public getTableauTrustedToken(): Promise<ITableauAuthTicketResponse> {
    return this.api.get<ITableauAuthTicketResponse>(`tableau_broker/ticket`)
      .pipe(
        map(extractResponseBody),
      ).toPromise();
  }

  private refreshTrustedToken(): void {
    const intervalDuration = 300000; // refreshing token after every 5 minutes
    this.tokenRefreshProcessStarted = true;
    this.sessionRefreshPoolingSubscription = interval(intervalDuration)
      .pipe(
        switchMap(() => this.authThenLoad(
          this.currentVizArguments.serverBaseUrl,
          this.currentVizArguments.tableauSite,
          this.currentVizArguments.workbook,
          this.currentVizArguments.view,
          true,
        )),
      ).subscribe();
  }

  private buildAuthUrlPng(serverBaseUrl: string, tableauSite: string, workbook: string, view: string, tkt: string): string {
    let redemptionUrl: string;
    if (!tableauSite) {
      redemptionUrl = serverBaseUrl + '/trusted/' + tkt + '/views/' + workbook + '/' + view + '.png';
    } else {
      redemptionUrl = serverBaseUrl + '/trusted/' + tkt + '/t/' + tableauSite + '/views/' + workbook + '/' + view + '.png';
    }
    return redemptionUrl;
  }

  private imgLoadPromise(url: string): Promise<any> {
    return new Promise(function (resolve, reject) {
      const redemptionImg = new Image();
      redemptionImg.onload = () => {
        resolve(true);
      };
      redemptionImg.onerror = () => {
        reject(new Error(IMAGE_REDEMPTION_FAILURE_MESSAGE));
      };
      redemptionImg.src = url;
    });
  }

  private setCookie(cookieName: CookieKey, cookieValue: string | number, expiryDays: number): void {
    let expires: number;
    const date: Date = new Date();
    date.setTime(date.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
    if (expiryDays === 0) {
      expires = 0;
    } else {
      expires = +date.toUTCString();
    }
    document.cookie = cookieName + '=' + cookieValue + ';' + expires + ';path=/';
  }

  private getCookie(cookieName: CookieKey): string {
    const name = cookieName + '=';
    const decodedCookie: string = decodeURIComponent(document.cookie);
    const authCookieItems: string[] = decodedCookie.split(';');
    for (let item of authCookieItems) {
      while (item.charAt(0) == ' ') {
        item = item.substring(1);
      }
      if (item.indexOf(name) == 0) {
        return item.substring(name.length, item.length);
      }
    }
  }

  private extendTimeoutCookie(): void {
    const date: Date = new Date();
    date.setTime(date.getTime() + (PING_MINUTES * 60 * 1000));
    this.setCookie(TABLEAU_TIME_OUT_COOKIE, date.valueOf(), 0);
  }

  private isAuthExpired(): boolean {
    const cookieTimeToLive: string = this.getCookie(TABLEAU_TIME_OUT_COOKIE);
    if (!cookieTimeToLive) return true;
    const currentTime = Date.now();
    const millisecondsSinceLastCookieSet = currentTime - Number(cookieTimeToLive);
    return millisecondsSinceLastCookieSet >= PING_MILLI_SECONDS;
  }
}
